import React, { useEffect, useRef, useState } from 'react';
import { useNavigationPending } from '../context/NavigationContext';

/**
 * Ultra-thin animated progress bar at the very top of the viewport.
 * Shows during React transition-based navigations (useTransition).
 * Inspired by NProgress / YouTube — refined for a minimal, polished feel.
 */
const NavigationProgress = () => {
  const isPending = useNavigationPending();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (isPending) {
      setVisible(true);
      setProgress(20);

      // Smooth deceleration — fast start, slows as it approaches 90%
      intervalRef.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) return prev;
          const remaining = 90 - prev;
          const increment = Math.max(remaining * 0.08, 0.5);
          return Math.min(prev + increment, 90);
        });
      }, 200);
    } else if (visible) {
      clearInterval(intervalRef.current);
      setProgress(100);
      timeoutRef.current = setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 250);
    }

    return () => {
      clearInterval(intervalRef.current);
      clearTimeout(timeoutRef.current);
    };
  }, [isPending]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible && progress === 0) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] pointer-events-none"
      style={{ height: 2 }}
    >
      <div
        className="h-full rounded-r-full"
        style={{
          width: `${progress}%`,
          background: 'linear-gradient(90deg, rgba(192,240,80,0.6) 0%, #C0F050 60%, rgba(192,240,80,0.8) 100%)',
          transition: progress === 100
            ? 'width 180ms ease-out, opacity 200ms ease-out 100ms'
            : 'width 300ms cubic-bezier(0.22, 1, 0.36, 1)',
          opacity: progress === 100 ? 0 : 1,
          boxShadow: '0 0 6px rgba(192, 240, 80, 0.3)',
        }}
      />
    </div>
  );
};

export default NavigationProgress;
